/**
 * A2UIChat - Library Entry Point
 * 
 * Unified chat component for Dash and vanilla JS contexts.
 * 
 * Exports:
 * - A2UIChat: Main chat class with Canvas-style layout
 * - A2UIChatGlobal: Vanilla JS wrapper with imperative API
 * 
 * Features:
 * - Sentry error tracking in production
 * 
 * Usage (vanilla JS):
 * ```javascript
 * import { A2UIChatGlobal } from 'dash_chat_panel/global';
 * 
 * const chat = new A2UIChatGlobal({
 *     apiEndpoint: '/api',
 *     workspaceId: 'ws-123',
 *     canvasId: 'canvas-456',
 *     accessToken: 'token',
 *     layoutMode: 'full',
 *     theme: 'dark',
 * });
 * 
 * await chat.init(document.getElementById('container'));
 * ```
 * 
 * Usage (Dash):
 * ```python
 * from dash_chat_panel import DashA2UIChat
 * 
 * chat = DashA2UIChat(
 *     id='my-chat',
 *     workspace_id='ws-123',
 *     canvas_id='canvas-456',
 *     layout_mode='full',
 *     theme='dark',
 * )
 * ```
 */

// Import Sentry for error tracking
import { initSentry } from '../../../lib/js/sentry.js';

// Initialize Sentry for production environment
const SENTRY_DSN = typeof window !== 'undefined' && window.SENTRY_EXTENSIONS_DSN 
    ? window.SENTRY_EXTENSIONS_DSN 
    : (typeof process !== 'undefined' && process.env?.SENTRY_EXTENSIONS_DSN);

if (SENTRY_DSN) {
    initSentry({
        dsn: SENTRY_DSN,
        extension: 'chat',
        version: '1.0.0',
        tracesSampleRate: 0.1,
    }).then(enabled => {
        if (enabled) {
            console.log('[A2UIChat] Sentry error tracking enabled');
        }
    });
}

// Main exports
export { A2UIChat } from './A2UIChat.js';
export { A2UIChatGlobal } from './A2UIChatGlobal.js';

// Default export for convenience
export { A2UIChatGlobal as default } from './A2UIChatGlobal.js';

